/* eslint-disable sonarjs/no-duplicate-string */
import { ConservationBenchmarkType } from '@/api/rest/resources/conservation';
import { BenchmarkType, ConservationBenchmark, PopulationDensityBenchmark } from '@/api/rest/resources/types/fact';
import { Logger } from '@/lib/logs/logger';

import { resolveThemeColor } from './resolveThemeColor';

export type BenchmarkStyle = {
  primary: string;
  text: string;
};

const exhaustivenessCheck = (benchmark: never): BenchmarkStyle => {
  Logger.error(`No color defined for benchmark type "${benchmark}"`);
  return { primary: resolveThemeColor('transparent'), text: resolveThemeColor('transparent') };
};

export const getStyleForBenchmark = (
  benchmark: BenchmarkType | ConservationBenchmark | PopulationDensityBenchmark,
): BenchmarkStyle => {
  switch (benchmark) {
    case BenchmarkType.unchanged:
      return {
        primary: resolveThemeColor('benchmark-unchanged-text'),
        text: resolveThemeColor('benchmark-unchanged-background'),
      };
    case BenchmarkType.very_low:
      return { primary: resolveThemeColor('error'), text: resolveThemeColor('error-light') };
    case BenchmarkType.low:
      return { primary: resolveThemeColor('error-light'), text: resolveThemeColor('error-dark') };
    case BenchmarkType.moderate:
      return { primary: resolveThemeColor('warning-light'), text: resolveThemeColor('warning-dark') };
    case BenchmarkType.high:
      return { primary: resolveThemeColor('success-light'), text: resolveThemeColor('success-dark') };
    case ConservationBenchmark.pristine:
      return {
        primary: resolveThemeColor('conservation-benchmark-pristine-background'),
        text: resolveThemeColor('conservation-benchmark-pristine-text'),
      };
    case ConservationBenchmark.good:
      return {
        primary: resolveThemeColor('conservation-benchmark-good-background'),
        text: resolveThemeColor('conservation-benchmark-good-text'),
      };
    case ConservationBenchmark.fair:
      return {
        primary: resolveThemeColor('conservation-benchmark-fair-background'),
        text: resolveThemeColor('conservation-benchmark-fair-text'),
      };
    case ConservationBenchmark.poor:
      return {
        primary: resolveThemeColor('conservation-benchmark-poor-background'),
        text: resolveThemeColor('conservation-benchmark-poor-text'),
      };
    case ConservationBenchmark.critical:
      return {
        primary: resolveThemeColor('conservation-benchmark-critical-background'),
        text: resolveThemeColor('conservation-benchmark-critical-text'),
      };
    case PopulationDensityBenchmark.rural:
      return {
        primary: resolveThemeColor('population-density-benchmark-rural-background'),
        text: resolveThemeColor('population-density-benchmark-rural-text'),
      };
    case PopulationDensityBenchmark.urban_cluster:
      return {
        primary: resolveThemeColor('population-density-benchmark-urban-cluster-background'),
        text: resolveThemeColor('population-density-benchmark-urban-cluster-text'),
      };
    case PopulationDensityBenchmark.urban_center:
      return {
        primary: resolveThemeColor('population-density-benchmark-urban-center-background'),
        text: resolveThemeColor('population-density-benchmark-urban-center-text'),
      };
    case BenchmarkType.alerted:
      return { primary: resolveThemeColor('error-light'), text: resolveThemeColor('error-dark') };
    case BenchmarkType.stable:
      return { primary: resolveThemeColor('success-light'), text: resolveThemeColor('success-dark') };
    default:
      return exhaustivenessCheck(benchmark);
  }
};

type ConservationBenchmarkStyle = {
  background: string;
  color: string;
};

const exhaustivenessConservationBenchmarkCheck = (benchmark: never): ConservationBenchmarkStyle => {
  Logger.error(`No color defined for benchmark type "${benchmark}"`);
  return { background: resolveThemeColor('transparent'), color: resolveThemeColor('transparent') };
};

export const getStyleForConservationBenchmark = (
  benchmark: ConservationBenchmarkType | never,
): ConservationBenchmarkStyle => {
  switch (benchmark) {
    case ConservationBenchmarkType.thriving:
      return {
        background: '#BFFFE6',
        color: '#285E2B',
      };
    case ConservationBenchmarkType.healthy:
      return {
        background: '#CFFFBF',
        color: '#285E2B',
      };
    case ConservationBenchmarkType.managing:
      return {
        background: '#E8F5B2',
        color: '#004C3D',
      };
    case ConservationBenchmarkType.fragile:
      return {
        background: '#FFF2D2',
        color: '#774200',
      };
    case ConservationBenchmarkType.degraded:
      return {
        background: '#F6D4C1',
        color: '#774200',
      };
    default:
      return exhaustivenessConservationBenchmarkCheck(benchmark);
  }
};
